// packages/engine/src/process-ir-desugar.ts
//
// The two walk-only IR desugarings shared by the render emitter
// (process-step-emitter.ts) and the trace producer (process-trace-producer.ts).
// Both are printer-invisible (the printer round-trips the authored sugar from
// the IR); they exist so the linear walk/graph shape reflects the runtime
// semantics. Extracted here so the producer applies them byte-for-byte
// identically to the emitter — a single source of truth, no drift.

import type {
  Flow,
  ProcessNode,
  AwaitNode,
  ParallelNode,
} from "./process-model.js";

/**
 * v0.9.0 §2.5: a spliced process (or the root flow) with a MULTI-await head
 * keeps its any-of trigger semantics by demoting the leading awaits to a
 * `parallel race` over them — two sequential waits would flip OR into AND. A
 * single leading await demotes to a plain wait positionally (no transform);
 * prose in the leading run stays ahead of the race.
 */
export function demoteMultiAwaitHead(flow: Flow): Flow {
  let i = 0;
  const lead: ProcessNode[] = [];
  while (i < flow.length && (flow[i]!.kind === "await" || flow[i]!.kind === "prose")) {
    lead.push(flow[i]!);
    i++;
  }
  const awaits = lead.filter((n) => n.kind === "await") as AwaitNode[];
  if (awaits.length < 2) return flow;
  const prose = lead.filter((n) => n.kind === "prose");
  const race: ParallelNode = {
    kind: "parallel",
    owner: awaits[0]!.owner,
    merge: { detached: false, policy: "race" },
    body: { form: "branches", branches: awaits.map((a) => ({ body: [a] })) },
    stepId: { path: [], contentKey: "parallel" },
  };
  return [...prose, race, ...flow.slice(i)];
}

/**
 * v0.9.0 §2.2: the `parallel await <head> { W } else { E }` boundary clause
 * desugars (walk/graph only) to a synthetic timer branch: `await <head>`
 * followed by the else body, or a synthesized `fail "<head>"` when no else was
 * written. Returns the timer branch's flow, appended as the last parallel
 * branch (index `body.branches.length`) by both callers.
 */
export function awaitClauseTimerFlow(n: ParallelNode): readonly ProcessNode[] {
  if (!n.awaitClause) return [];
  return [
    {
      kind: "await",
      owner: n.owner,
      target: n.awaitClause.head,
      stepId: { path: [], contentKey: "await" },
    } as ProcessNode,
    ...(n.awaitClause.elseBody ??
      ([
        {
          kind: "terminator",
          outcome: "fail",
          reason: n.awaitClause.head,
          stepId: { path: [], contentKey: "fail" },
        },
      ] as readonly ProcessNode[])),
  ];
}
